import { IconRalphWiggum } from "@/assets/icons/IconRalphWiggum";
import { Markdown } from "@/components/ai/chat/Markdown";
import { parseAgentModePrefix, type AgentModePrefix } from "@/components/flexkit/chat/agent-mode-prefix";
import { cn } from "@/components/podkit/lib/cn";
import type { UserMessage } from "@/hooks/use-stream-conversation";
import { IconClock } from "central-icons/IconClock";
import { IconSearchlinesSparkle } from "@central-icons-react/round-outlined-radius-2-stroke-1.5/IconSearchlinesSparkle";
import { IconCodeAssistant } from "@central-icons-react/round-outlined-radius-2-stroke-1.5/IconCodeAssistant";
import { IconFork } from "@central-icons-react/round-outlined-radius-2-stroke-1.5/IconFork";
import { IconMagnifyingGlass } from "@central-icons-react/round-outlined-radius-2-stroke-1.5/IconMagnifyingGlass";
import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import { parseSubagentAttribution, parseSubagentName } from "@/utils/subagent";
import { useGetAgentExecution } from "@/queries/agent-queries";
import { ImageLightbox, type LightboxImage } from "@/components/ai/chat/ImageLightbox";
import { ImageThumbnail } from "@/components/ai/chat/ImageThumbnail";

const modeConfig = {
    agent: {
        label: "Agent",
        Icon: IconCodeAssistant,
        colorClass: "text-content-brand",
    },
    spec: {
        label: "Plan",
        Icon: IconSearchlinesSparkle,
        colorClass: "text-content-brand-accent-01",
    },
    ralph: {
        label: "Ralph",
        Icon: IconRalphWiggum,
        colorClass: "text-content-orange",
    },
    review: {
        label: "Review",
        Icon: IconMagnifyingGlass,
        colorClass: "text-content-brand",
    },
};

const AgentModeBadge: FC<{ mode: AgentModePrefix }> = ({ mode }) => {
    const { label, Icon, colorClass } = modeConfig[mode];

    return (
        <div className={cn("flex items-center gap-1 font-normal", colorClass)}>
            <Icon size={14} className="shrink-0" />
            <span className="text-xs">{label}</span>
        </div>
    );
};

const SubagentBadge: FC<{ executionId: string }> = ({ executionId }) => {
    const { data: execution } = useGetAgentExecution(executionId);

    // Extract task name from subagent execution name
    const taskName = execution?.metadata?.name ? parseSubagentName(execution.metadata.name)?.task : undefined;

    const displayName = taskName || `Subagent`;

    return (
        <div className="flex items-center gap-1 font-normal text-content-secondary">
            <IconFork size={14} className="shrink-0" />
            <span className="text-xs">From: {displayName}</span>
        </div>
    );
};

function useBlobUrl(data: Uint8Array, mimeType: string): string {
    const [imageUrl, setImageUrl] = useState<string>("");

    useEffect(() => {
        if (!data || data.length === 0) {
            return;
        }

        // Handle case where data might be a regular array or object instead of Uint8Array
        let blobData: BlobPart;
        if (data instanceof Uint8Array) {
            blobData = data;
        } else if (Array.isArray(data)) {
            blobData = new Uint8Array(data);
        } else if (typeof data === "object" && data !== null) {
            // Protobuf-es might return an object with numeric keys
            const values = Object.values(data as Record<string, number>);
            blobData = new Uint8Array(values);
        } else {
            return;
        }

        const blob = new Blob([blobData], { type: mimeType || "image/png" });
        const url = URL.createObjectURL(blob);
        setImageUrl(url);

        return () => {
            URL.revokeObjectURL(url);
        };
    }, [data, mimeType]);

    return imageUrl;
}

type ImageFragment = {
    id: string;
    data: Uint8Array;
    mimeType: string;
};

const UserImageGallery: FC<{ images: ImageFragment[] }> = ({ images }) => {
    const [lightboxIndex, setLightboxIndex] = useState(-1);
    const [blobUrls, setBlobUrls] = useState<Map<string, string>>(new Map());
    const isLightboxOpen = lightboxIndex >= 0;

    // Track blob URLs as they're created by child components
    const registerUrl = useCallback((id: string, url: string) => {
        setBlobUrls((prev) => {
            if (prev.get(id) === url) return prev;
            const next = new Map(prev);
            next.set(id, url);
            return next;
        });
    }, []);

    const lightboxImages: LightboxImage[] = useMemo(
        () =>
            images
                .map((img) => {
                    const url = blobUrls.get(img.id);
                    // TODO: the image fragment type doesn't carry a filename — once it does,
                    // use it here so the lightbox header shows a meaningful name.
                    return url ? { src: url, alt: `User uploaded image` } : null;
                })
                .filter((img): img is LightboxImage => img !== null),
        [images, blobUrls],
    );

    const handleOpenLightbox = useCallback((index: number) => {
        setLightboxIndex(index);
    }, []);

    const handleCloseLightbox = useCallback((open: boolean) => {
        if (!open) setLightboxIndex(-1);
    }, []);

    if (images.length === 0) return null;

    const gridClass =
        images.length === 1 ? "grid-cols-1" : images.length === 2 ? "grid-cols-2" : "grid-cols-2 sm:grid-cols-3";

    return (
        <>
            <div className={cn("grid gap-2", gridClass)}>
                {images.map((img, index) => (
                    // eslint-disable-next-line gitpod-dashboard/require-onclick-tracking-id -- tracking ID is on the inner button
                    <UserImageThumbnailWithRegistration
                        key={img.id}
                        image={img}
                        index={index}
                        onClick={handleOpenLightbox}
                        onUrlReady={registerUrl}
                    />
                ))}
            </div>
            {lightboxImages.length > 0 && (
                <ImageLightbox
                    images={lightboxImages}
                    currentIndex={isLightboxOpen ? lightboxIndex : 0}
                    onIndexChange={setLightboxIndex}
                    open={isLightboxOpen}
                    onOpenChange={handleCloseLightbox}
                />
            )}
        </>
    );
};

const UserImageThumbnailWithRegistration: FC<{
    image: ImageFragment;
    index: number;
    onClick: (index: number) => void;
    onUrlReady: (id: string, url: string) => void;
}> = ({ image, index, onClick, onUrlReady }) => {
    const url = useBlobUrl(image.data, image.mimeType);

    useEffect(() => {
        if (url) {
            onUrlReady(image.id, url);
        }
    }, [url, image.id, onUrlReady]);

    if (!url) return null;
    return (
        // eslint-disable-next-line gitpod-dashboard/require-onclick-tracking-id -- tracking ID is on the inner button
        <ImageThumbnail
            src={url}
            alt={`User uploaded image ${index + 1}`}
            onClick={() => onClick(index)}
            className="h-24 w-full"
        />
    );
};

/**
 * Process a text fragment to extract subagent attribution if present.
 * Returns the content to display and optionally the subagent execution ID.
 */
function processTextFragment(content: string): { displayContent: string; subagentExecutionId?: string } {
    // First check for subagent attribution
    const subagentAttribution = parseSubagentAttribution(content);
    if (subagentAttribution) {
        // Strip the attribution prefix and return the message content
        // Also strip any agent mode prefix from the actual message
        const { strippedContent } = parseAgentModePrefix(subagentAttribution.messageContent);
        return {
            displayContent: strippedContent,
            subagentExecutionId: subagentAttribution.executionId,
        };
    }

    // No subagent attribution, just strip agent mode prefix
    const { strippedContent } = parseAgentModePrefix(content);
    return { displayContent: strippedContent };
}

export const UserMsg: FC<{
    message: UserMessage;
    isPending: boolean;
}> = ({ message, isPending }) => {
    // Check if any fragment has an agent mode prefix
    const agentMode = message.fragments.reduce<AgentModePrefix>((found, fragment) => {
        if (found !== "agent") return found;
        if (fragment.type === "text") {
            // Check for subagent attribution first, then agent mode
            const subagentAttribution = parseSubagentAttribution(fragment.content);
            const contentToCheck = subagentAttribution?.messageContent ?? fragment.content;
            return parseAgentModePrefix(contentToCheck).mode;
        }
        return "agent";
    }, "agent");

    // Check if any fragment has subagent attribution
    const subagentExecutionId = message.fragments.reduce<string | undefined>((found, fragment) => {
        if (found) return found;
        if (fragment.type === "text") {
            const subagentAttribution = parseSubagentAttribution(fragment.content);
            return subagentAttribution?.executionId;
        }
        return undefined;
    }, undefined);

    return (
        // the class user-message is used within the markdown component to style some markdown elements differently when presented
        // inside a user message.
        <div
            className="user-message flex w-fit max-w-full flex-col items-end gap-1 self-end"
            data-message-id={`${message.type}-${message.id}`}
        >
            {subagentExecutionId ? (
                <SubagentBadge executionId={subagentExecutionId} />
            ) : (
                <AgentModeBadge mode={agentMode} />
            )}
            <div
                className={cn(
                    "min-w-0 max-w-full flex-1 rounded-xl border border-border-subtle bg-surface-muted px-3 py-2 text-base text-content-primary shadow-sm transition-opacity",
                    {
                        "opacity-50": isPending,
                    },
                )}
            >
                <div className="flex gap-2">
                    {isPending && <IconClock className="mt-[1px] shrink-0 text-content-primary" size="14" />}
                    <div className="flex min-w-0 flex-1 flex-col gap-2">
                        {message.fragments.map((fragment) => {
                            if (fragment.type === "text") {
                                const { displayContent } = processTextFragment(fragment.content);
                                return <Markdown content={displayContent} key={fragment.id} />;
                            }
                            return null;
                        })}
                        <UserImageGallery
                            images={message.fragments.filter(
                                (f): f is ImageFragment & { type: "image" } => f.type === "image",
                            )}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
